import { faBuilding, faCopy, faPen, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EmailCampaign, EmailCampaignStatus } from '@spike/email-marketing-model';
import { Chip } from 'components/UI/V3/Chip/Chip';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import moment from 'moment-timezone';
import { FunctionComponent } from 'react';
import { SHOW_REVENUE_COLUMN } from '../campaignsListFeatureFlags';
import styles from './EmailCampaignsRow.module.scss';

interface EmailCampaignsRowProps {
    campaign: EmailCampaign;
    onEditCampaign: () => void;
    onDuplicateCampaign: () => void;
    onDeleteCampaign: () => void;
    /**
     * Fires when the row itself is clicked. Only invoked for `sent` campaigns —
     * the parent decides where to route (typically the Campaign Detail view).
     * Undefined for any other status so drafts / scheduled don't try to open a
     * detail that doesn't yet exist.
     */
    onViewDetail?: () => void;
}

const STATUS_LABEL: Record<EmailCampaignStatus, string> = {
    draft: 'Draft',
    scheduled: 'Scheduled',
    sending: 'Sending',
    sent: 'Sent',
    failed: 'Failed'
};

/**
 * What goes in the Delivered column. For sent campaigns we show the number of
 * successful deliveries (the value the user is most likely to track). For any
 * other status we show the status label as a stand-in so the column is never
 * blank.
 */
const formatDelivered = (campaign: EmailCampaign): string | number => {
    if (campaign.statusId === 'sent') return campaign.sentCount;
    return STATUS_LABEL[campaign.statusId];
};

/**
 * Backend returns rates as decimals in [0, 1]. They're meaningful only once
 * the campaign has been sent — earlier statuses always have rate 0, which we
 * render as "-" to avoid implying "0% engagement" instead of "not applicable".
 */
const formatRate = (rate: number, campaign: EmailCampaign): string => {
    if (campaign.statusId !== 'sent' && campaign.statusId !== 'failed') return '-';
    if (campaign.sentCount === 0) return '-';
    return `${(rate * 100).toFixed(1)}%`;
};

/**
 * Send date — only meaningful after the campaign has been processed. For
 * drafts and scheduled-but-not-yet-sent we show "-".
 *
 * Backend does not expose a `scheduled_at` field today, so a campaign that's
 * "scheduled" also renders "-"; if/when the backend ships it, we surface it
 * here and the column starts showing future dates too.
 */
const formatSendDate = (campaign: EmailCampaign): string => {
    const iso = campaign.sentAt ?? campaign.failedAt;
    if (!iso) return '-';
    const m = moment(iso);
    return m.isValid() ? m.format('MMM DD, YYYY [at] hh:mm A') : '-';
};

const EmailCampaignsRow: FunctionComponent<EmailCampaignsRowProps> = ({
    campaign,
    onEditCampaign,
    onDuplicateCampaign,
    onDeleteCampaign,
    onViewDetail
}) => {
    const isDraft = campaign.statusId === 'draft';
    const isSent = campaign.statusId === 'sent';
    const canOpenDetail = isSent && Boolean(onViewDetail);
    // Campaigns created from the Organization Portal are read-only here: the
    // backend rejects edit, duplicate and delete, so the row has no actions.
    const isOrganizationCampaign = Boolean(campaign.sentByOrganization);

    // Row click opens the Campaign Detail — but only for sent campaigns.
    // Drafts route through the wizard via the dropdown "Edit" action, so their
    // rows stay non-interactive to avoid confusion between "click row = open
    // detail" and "click row = resume draft".
    const handleRowClick = canOpenDetail ? () => onViewDetail?.() : undefined;

    return (
        <TableRow
            onClick={handleRowClick}
            className={canOpenDetail ? styles.clickable : undefined}
        >
            <TableCell
                className={styles.nameCell}
                title={campaign.name}
            >
                <span className={styles.name}>{campaign.name}</span>
                {isOrganizationCampaign && (
                    <div className={styles.origin}>
                        <Chip
                            icon={faBuilding}
                            label={campaign.originLabel ?? 'Sent by organization'}
                        />
                    </div>
                )}
            </TableCell>
            <TableCell>{formatDelivered(campaign)}</TableCell>
            <TableCell>{formatRate(campaign.openRate, campaign)}</TableCell>
            <TableCell>{formatRate(campaign.clickRate, campaign)}</TableCell>
            {/*
             * Revenue is intentionally a placeholder — the backend does not
             * track per-campaign revenue today. Leaving the column in the
             * layout so we can wire it without a structural change later.
             * Gated by `SHOW_REVENUE_COLUMN` so header + row stay in sync;
             * both hide together when the flag flips off.
             */}
            {SHOW_REVENUE_COLUMN && <TableCell>-</TableCell>}
            <TableCell>{formatSendDate(campaign)}</TableCell>
            <TableCell isActions>
                {/*
                 * `stopPropagation` sits on the wrapper (not inside the
                 * DropdownTrigger's button) because DropdownTrigger listens
                 * for the click on its own wrapping div — stopping the event
                 * before it reaches that div would prevent the dropdown from
                 * ever opening. Placing it here lets the trigger open
                 * normally while still shielding the row's onClick (which
                 * opens the Detail view for sent campaigns) from firing.
                 */}
                {!isOrganizationCampaign && (
                    <div onClick={event => event.stopPropagation()}>
                        <Dropdown>
                            <DropdownTrigger>
                                <button
                                    type="button"
                                    className={styles.actionsButton}
                                >
                                    <FontAwesomeIcon icon={faEllipsis} />
                                </button>
                            </DropdownTrigger>

                            <DropdownContent
                                minWidth={130}
                                align="end"
                            >
                                {/*
                                 * Edit and Delete only make sense for drafts; the
                                 * backend rejects update/destroy on any other status
                                 * with 422. Hiding the actions avoids the dead-end UX.
                                 */}
                                {isDraft && (
                                    <DropdownItem
                                        leftIcon={faPen}
                                        onSelect={onEditCampaign}
                                    >
                                        Edit
                                    </DropdownItem>
                                )}
                                <DropdownItem
                                    leftIcon={faCopy}
                                    onSelect={onDuplicateCampaign}
                                >
                                    Duplicate
                                </DropdownItem>
                                {isDraft && (
                                    <DropdownItem
                                        leftIcon={faTrashAlt}
                                        onSelect={onDeleteCampaign}
                                    >
                                        Delete
                                    </DropdownItem>
                                )}
                            </DropdownContent>
                        </Dropdown>
                    </div>
                )}
            </TableCell>
        </TableRow>
    );
};

export default EmailCampaignsRow;
